import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type EnrollBeginResponse,
  disableMfa,
  enrollBegin,
  enrollConfirm,
  getMfaStatus,
} from "@/api/mfa";

export function MfaPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ["mfa-status"],
    queryFn: getMfaStatus,
  });

  const [enrollment, setEnrollment] = useState<EnrollBeginResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollError, setEnrollError] = useState<string | null>(null);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);

  const beginMutation = useMutation({
    mutationFn: enrollBegin,
    onSuccess: (data) => {
      setEnrollment(data);
      setEnrollCode("");
      setEnrollError(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setEnrollError(detail || "error");
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) => enrollConfirm(code),
    onSuccess: () => {
      setEnrollment(null);
      setEnrollCode("");
      setEnrollError(null);
      qc.invalidateQueries({ queryKey: ["mfa-status"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setEnrollError(
        detail === "invalid_code" ? t("mfa.invalidCode") : detail || "error",
      );
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableMfa(disablePwd, disableCode),
    onSuccess: () => {
      setDisableOpen(false);
      setDisablePwd("");
      setDisableCode("");
      setDisableError(null);
      qc.invalidateQueries({ queryKey: ["mfa-status"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      if (detail === "wrong_password") setDisableError(t("mfa.wrongPassword"));
      else if (detail === "invalid_code") setDisableError(t("mfa.invalidCode"));
      else setDisableError(detail || "error");
    },
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <SecurityIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("mfa.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("mfa.subtitle")}
            </Typography>
          </Box>
          {status.data && (
            <Chip
              color={status.data.enabled ? "success" : "default"}
              label={
                status.data.enabled ? t("mfa.enabled") : t("mfa.disabled")
              }
            />
          )}
        </Stack>

        {status.isLoading ? (
          <CircularProgress size={20} />
        ) : status.data?.enabled ? (
          <Stack spacing={2}>
            <Alert severity="success">{t("mfa.activeNotice")}</Alert>
            <Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDisableOpen(true)}
              >
                {t("mfa.disable")}
              </Button>
            </Box>
          </Stack>
        ) : enrollment ? (
          <Stack spacing={2}>
            <Alert severity="info">{t("mfa.scanQrThenCode")}</Alert>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  p: 1,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              >
                <img
                  src={enrollment.qr_png_data_url}
                  alt="QR code"
                  width={180}
                  height={180}
                  style={{ display: "block" }}
                />
              </Box>
              <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("mfa.secretManualEntry")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 14,
                      letterSpacing: 1,
                      wordBreak: "break-all",
                      bgcolor: "action.hover",
                      p: 1,
                      borderRadius: 0.5,
                    }}
                  >
                    {enrollment.secret}
                  </Typography>
                </Box>
                <TextField
                  label={t("mfa.confirmCodeLabel")}
                  value={enrollCode}
                  onChange={(e) =>
                    setEnrollCode(e.target.value.replace(/\s+/g, "").slice(0, 8))
                  }
                  inputMode="numeric"
                  helperText={t("mfa.confirmCodeHelp")}
                  fullWidth
                />
                {enrollError && <Alert severity="error">{enrollError}</Alert>}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => confirmMutation.mutate(enrollCode)}
                    disabled={confirmMutation.isPending || enrollCode.length < 6}
                  >
                    {t("mfa.confirmActivate")}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setEnrollment(null);
                      setEnrollCode("");
                      setEnrollError(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("mfa.inactiveNotice")}
            </Typography>
            {enrollError && <Alert severity="error">{enrollError}</Alert>}
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => beginMutation.mutate()}
                disabled={beginMutation.isPending}
                startIcon={<SecurityIcon />}
              >
                {t("mfa.enable")}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>

      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("mfa.disableTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("mfa.disableBody")}
          </DialogContentText>
          <Stack spacing={2}>
            {disableError && <Alert severity="error">{disableError}</Alert>}
            <TextField
              label={t("mfa.passwordLabel")}
              type="password"
              value={disablePwd}
              onChange={(e) => setDisablePwd(e.target.value)}
              fullWidth
              autoComplete="current-password"
            />
            <TextField
              label={t("mfa.confirmCodeLabel")}
              value={disableCode}
              onChange={(e) =>
                setDisableCode(e.target.value.replace(/\s+/g, "").slice(0, 8))
              }
              inputMode="numeric"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => disableMutation.mutate()}
            disabled={
              disableMutation.isPending ||
              !disablePwd ||
              disableCode.length < 6
            }
          >
            {t("mfa.confirmDisable")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
